import { ReactNode } from 'react';
import { Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';
import dayjs from 'dayjs';

import RobotoRegular from '@/assets/fonts/Roboto-Regular.ttf';
import RobotoBold from '@/assets/fonts/Roboto-Bold.ttf';

import RasmalReportFooterLogo from './RasmalReportFooterLogo';
import { useTranslation } from 'react-i18next';

Font.register({
  family: 'Roboto',
  fonts: [
    { src: RobotoRegular, fontStyle: 'normal', fontWeight: 'normal' },
    { src: RobotoBold, fontStyle: 'normal', fontWeight: 'bold' },
  ],
});

const currentDate = dayjs(new Date()).format('DD/MM/YYYY');

const styles = StyleSheet.create({
  page: {
    padding: '16px 24px 36px 16px',
    fontFamily: 'Roboto',
  },

  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    fontFamily: 'Janna',
  },

  headerLeftElements: {
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
  },

  leftCornerHeaderContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },

  verticalDivider: {
    width: 4,
    height: 32,
    backgroundColor: '#D81059',
  },

  leftCornerHeaderCompanyName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#2A186A',
  },

  leftCornerHeaderCompanyInfo: {
    fontSize: 10,
    color: '#868EA1',
  },

  headerRightElements: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },

  headerReportName: {
    fontSize: 10,
    fontFamily: 'Janna',
    color: '#2A186A',
    fontWeight: 'bold',
  },

  headerPageNumberContainer: {
    width: 16,
    height: 16,
    borderRadius: 1,
    backgroundColor: '#D81059',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  headerPageNumber: {
    fontWeight: 'bold',
    fontSize: 10,
    color: '#fff',
  },

  footer: {
    backgroundColor: '#2A186A',
    paddingHorizontal: 20,
    paddingVertical: 4,
    height: 36,
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTop: '3px solid #6430DA',
  },

  poweredBySection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },

  poweredByText: {
    fontSize: 10,
    color: '#fff',
    fontFamily: 'Janna',
  },

  website: {
    fontSize: 12,
    color: '#fff',
  },
});

export type ReportHeaderInfo = {
  company: {
    name: string;
    currency: string;
  };
  user: {
    email: string;
  };
};

const ReportTemplatePage = ({
  wrap = true,
  reportName,
  reportHeaderInfo,
  children,
  hasCurrency = true,
}: {
  wrap?: boolean;
  reportName: string;
  reportHeaderInfo: ReportHeaderInfo;
  children: ReactNode;
  hasCurrency?: boolean;
}) => {
  const { t, i18n } = useTranslation('reports', { keyPrefix: 'common' });

  const showCurrrency = () => {
    if (i18n.language === 'ar') {
      return (
        <>
          <Text style={styles.leftCornerHeaderCompanyInfo}>
            {`${reportHeaderInfo.user.email} | ${currentDate} |  ${
              hasCurrency ? (reportHeaderInfo.company.currency === 'SAR' ? 'ə' : reportHeaderInfo.company.currency) : ''
            }   ${hasCurrency ? t('currencyIs') : ''}`}
          </Text>
        </>
      );
    } else {
      return (
        <>
          <Text style={styles.leftCornerHeaderCompanyInfo}>
            {`${reportHeaderInfo.user.email} | ${hasCurrency ? t('currencyIs') : ''} ${
              hasCurrency
                ? reportHeaderInfo.company.currency === 'SAR'
                  ? 'SAR'
                  : reportHeaderInfo.company.currency
                : ''
            }${hasCurrency ? ' |' : ''}${currentDate}`}
          </Text>
        </>
      );
    }
  };

  return (
    <Page style={styles.page} wrap={wrap} size="A4">
      <View style={styles.header} fixed>
        <View style={styles.headerLeftElements}>
          <View style={styles.verticalDivider} />
          <View style={styles.leftCornerHeaderContent}>
            <Text style={styles.leftCornerHeaderCompanyName}>{reportHeaderInfo.company.name}</Text>
            {showCurrrency()}
          </View>
        </View>
        <View style={styles.headerRightElements}>
          <Text style={styles.headerReportName}>
            {t('report')} {reportName}
          </Text>
          <View
            render={({ pageNumber }) => (
              <View style={styles.headerPageNumberContainer}>
                <Text style={styles.headerPageNumber}>{pageNumber}</Text>
              </View>
            )}
          />
        </View>
      </View>

      <View>{children}</View>

      <View style={i18n.language === 'ar' ? [styles.footer, { flexDirection: 'row-reverse' }] : styles.footer} fixed>
        <View
          style={
            i18n.language === 'ar'
              ? { ...styles.poweredBySection, flexDirection: 'row-reverse' }
              : styles.poweredBySection
          }
        >
          <Text style={styles.poweredByText}>{t('poweredBy')}</Text>
          <RasmalReportFooterLogo />
        </View>
        <View>
          <Text style={styles.website}>www.rasmal.io</Text>
        </View>
      </View>
    </Page>
  );
};

export default ReportTemplatePage;
